import { SYNONYM_PREFS_QUERY_KEY } from "@/hooks/useUserPreference";
import { BookText } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import useSynonymGroups from "@/hooks/useSynonymGroups";
import useUserPreference from "@/hooks/useUserPreference";
import { SynonymGroup } from "@/models/synonym-group/synonymGroup";
import SynonymGroupItem from "./SynonymGroupItem";
import { Switch } from "@capacit-com/lara-law-shared-ui";
import { showErrorToast } from "../custom-toasts/toasts";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@capacit-com/lara-law-shared-ui";
import debounce from "lodash/debounce";
import { SynonymPreferences } from "@/models/users/UserPreference";
import { logger } from "@/lib/logger";

const SynonymButton: React.FC = () => {
  const { useQueryPagination } = useSynonymGroups();
  const {
    useQuerySynonymPreferences,
    updateSynonymPreference,
    updateSynonymMatching,
  } = useUserPreference();

  const queryClient = useQueryClient();

  const [synonymGroups, setSynonymGroups] = useState<SynonymGroup[]>([]);
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  const [updating, setUpdating] = useState<Record<string, boolean>>({});

  const [optimisticGlobal, setOptimisticGlobal] = useState<boolean | undefined>(
    undefined
  );
  const [updatingGlobal, setUpdatingGlobal] = useState(false);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    pageNumber: 1,
    pageSize: 20,
    search: "",
    totalPages: 1,
    totalRecords: 0,
    hasMore: false,
    sortBy: "createdAt",
    sortAscending: false,
    filters: [
      {
        id: "isActive",
        value: true,
      },
      {
        id: "synonyms.active",
        value: true,
      },
    ],
  });

  const [searchInput, setSearchInput] = useState(paginationInfo.search ?? "");
  const [isSearchPending, setIsSearchPending] = useState(false);

  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setPaginationInfo((prev) => ({
          ...prev,
          search: value,
          pageNumber: 1,
        }));
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      applySearch.cancel();
    };
  }, [applySearch]);

  const fetchedSynonymGroups = useQueryPagination(
    {
      pageNumber: paginationInfo.pageNumber,
      pageSize: paginationInfo.pageSize,
      filters: paginationInfo.filters,
      sortBy: paginationInfo.sortBy,
      sortAscending: paginationInfo.sortAscending,
      search: paginationInfo.search,
    },
    undefined,
    {
      placeholderData: (previousData) => previousData,
    }
  );

  const synonymPrefsQuery = useQuerySynonymPreferences();

  const preferences = synonymPrefsQuery.data;
  const groupPrefs: Record<string, boolean> = useMemo(
    () => preferences?.synonymGroupsActive ?? {},
    [preferences]
  );
  const globalPref = preferences?.synonymMatchingActive ?? false;
  const effectiveUseSynonyms = optimisticGlobal ?? globalPref;

  useEffect(() => {
    if (fetchedSynonymGroups.data && fetchedSynonymGroups.isFetched) {
      setPaginationInfo((prev) => ({
        ...prev,
        totalPages: fetchedSynonymGroups.data.totalPages ?? prev.totalPages,
        totalRecords:
          fetchedSynonymGroups.data.totalRecords ?? prev.totalRecords,
        hasMore: fetchedSynonymGroups.data.hasMore ?? prev.hasMore,
      }));

      setSynonymGroups((prev) => {
        const base = paginationInfo.pageNumber === 1 ? [] : prev;
        const byId = new Map(base.map((g) => [g.id, g]));
        for (const g of fetchedSynonymGroups.data!.data) {
          byId.set(g.id, g);
        }
        return Array.from(byId.values());
      });

      setIsLoadingMore(false);
      setIsSearchPending(false);
    }
  }, [
    fetchedSynonymGroups.data,
    fetchedSynonymGroups.isFetched,
    paginationInfo.pageNumber,
  ]);

  // Restore scroll position after new items are loaded
  useEffect(() => {
    if (
      !isLoadingMore &&
      scrollPositionRef.current > 0 &&
      containerRef.current
    ) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = scrollPositionRef.current;
          scrollPositionRef.current = 0;
        }
      });
    }
  }, [isLoadingMore, synonymGroups]);

  const toggleGroup = useCallback(
    (group: SynonymGroup) => {
      const id = group.id;
      if (updating[id]) return;
      setUpdating((prev) => ({ ...prev, [id]: true }));
      const current = optimistic[id] ?? groupPrefs[id] ?? false;
      const next = !current;
      setOptimistic((prev) => ({ ...prev, [id]: next }));

      // Check if any other groups are active (excluding the current one)
      const otherGroupsActive = Object.entries({
        ...groupPrefs,
        ...optimistic,
        [id]: next, // Include the current group's new state
      }).some(([groupId, isActive]) => groupId !== id && isActive === true);

      // If activating the first group, also toggle global to true
      const shouldToggleGlobal =
        next && !otherGroupsActive && !effectiveUseSynonyms;

      if (shouldToggleGlobal) {
        setOptimisticGlobal(true);
        setUpdatingGlobal(true);
      }

      updateSynonymPreference.mutate(
        { synonymGroupId: id, active: next },
        {
          onError: () => {
            setOptimistic((prev) => {
              const copy = { ...prev };
              delete copy[id];
              return copy;
            });
            setUpdating((prev) => {
              const copy = { ...prev };
              delete copy[id];
              return copy;
            });
            if (shouldToggleGlobal) {
              setOptimisticGlobal(undefined);
              setUpdatingGlobal(false);
            }
            showErrorToast("Kunne ikke opdatere præference for termen", "Fejl");
          },
          onSuccess: () => {
            queryClient.setQueryData(
              SYNONYM_PREFS_QUERY_KEY,
              (old: SynonymPreferences) => {
                const prev = old ?? {
                  synonymMatchingActive: false,
                  synonymGroupsActive: {},
                };
                return {
                  ...prev,
                  synonymGroupsActive: {
                    ...prev.synonymGroupsActive,
                    [id]: next,
                  },
                };
              }
            );
            setUpdating((prev) => {
              const copy = { ...prev };
              delete copy[id];
              return copy;
            });
            setOptimistic((prev) => {
              const copy = { ...prev };
              delete copy[id];
              return copy;
            });

            // If we should toggle global, do it after the group update succeeds
            if (shouldToggleGlobal) {
              updateSynonymMatching.mutate(
                { active: true },
                {
                  onError: () => {
                    setOptimisticGlobal(undefined);
                    setUpdatingGlobal(false);
                    showErrorToast(
                      "Kunne ikke opdatere global synonym matching",
                      "Fejl"
                    );
                  },
                  onSuccess: () => {
                    queryClient.setQueryData(
                      SYNONYM_PREFS_QUERY_KEY,
                      (old: SynonymPreferences) => {
                        const prev = old ?? {
                          synonymMatchingActive: false,
                          synonymGroupsActive: {},
                        };
                        return {
                          ...prev,
                          synonymMatchingActive: true,
                        };
                      }
                    );
                    setOptimisticGlobal(undefined);
                    setUpdatingGlobal(false);
                  },
                }
              );
            }
          },
        }
      );
    },
    [
      updateSynonymPreference,
      updateSynonymMatching,
      groupPrefs,
      optimistic,
      updating,
      queryClient,
      effectiveUseSynonyms,
    ]
  );

  const toggleGlobal = useCallback(() => {
    if (updatingGlobal) return;
    const current = optimisticGlobal ?? globalPref;
    const next = !current;
    setOptimisticGlobal(next);
    setUpdatingGlobal(true);
    updateSynonymMatching.mutate(
      { active: next },
      {
        onError: () => {
          setOptimisticGlobal(undefined);
          setUpdatingGlobal(false);
          showErrorToast("Kunne ikke opdatere global synonym matching", "Fejl");
        },
        onSuccess: () => {
          queryClient.setQueryData(
            SYNONYM_PREFS_QUERY_KEY,
            (old: SynonymPreferences) => {
              const prev = old ?? {
                synonymMatchingActive: false,
                synonymGroupsActive: {},
              };
              return {
                ...prev,
                synonymMatchingActive: next,
              };
            }
          );
          setOptimisticGlobal(undefined);
          setUpdatingGlobal(false);
        },
      }
    );
  }, [
    updateSynonymMatching,
    globalPref,
    optimisticGlobal,
    updatingGlobal,
    queryClient,
  ]);

  const prefLoading =
    synonymPrefsQuery.isLoading ||
    updateSynonymPreference.isPending ||
    updateSynonymMatching.isPending;

  const loadMore = useCallback(async () => {
    if (!paginationInfo.hasMore || isLoadingMore) return;

    // Store scroll position before loading more
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }

    setIsLoadingMore(true);
    try {
      const nextPage = paginationInfo.pageNumber + 1;
      setPaginationInfo((prev) => ({
        ...prev,
        pageNumber: nextPage,
      }));
    } catch (error) {
      logger.error("Failed to load more synonym groups", {
        operation: "loadMoreSynonymGroups",
        error,
        pageNumber: paginationInfo.pageNumber,
      });
      showErrorToast(
        "Der skete en fejl under hentning af flere termer",
        "Fejl"
      );
      setIsLoadingMore(false);
    }
  }, [paginationInfo.hasMore, isLoadingMore, paginationInfo.pageNumber]);

  const observer = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && paginationInfo.hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, paginationInfo.hasMore, loadMore]
  );

  const isSearching = (paginationInfo.search ?? "").trim().length > 0;

  return (
    <div className="flex items-center justify-center">
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => {}}
          >
            <BookText className="size-4.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-w-80 p-0">
          <div className="flex flex-col">
            <div className="p-2 border-b flex justify-between items-center">
              <div className="flex gap-1">
                <h3 className="font-semibold text-sm">Dine termer</h3>
              </div>

              <div className="flex gap-1 items-center">
                <p className="text-xs">Match ved søgning</p>
                <Switch
                  checked={effectiveUseSynonyms}
                  onCheckedChange={toggleGlobal}
                  disabled={prefLoading || updatingGlobal}
                  aria-label={
                    effectiveUseSynonyms
                      ? "Deaktiver synonym matching"
                      : "Aktiver synonym matching"
                  }
                />
              </div>
            </div>

            <div className="p-2 border-b">
              <Input
                value={searchInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchInput(v);
                  setIsSearchPending(true);
                  applySearch(v);
                }}
                placeholder="Søg efter term eller synonym…"
                aria-label="Søg efter termer"
                className="h-8"
              />
            </div>

            <div ref={containerRef} className="max-h-96 overflow-y-auto">
              {(fetchedSynonymGroups.isFetching || isSearchPending) &&
              paginationInfo.pageNumber === 1 ? (
                <div className="p-4 flex items-center justify-center text-sm text-muted-foreground">
                  <Spinner className="size-4 mr-2" /> Søger…
                </div>
              ) : synonymGroups && synonymGroups.length > 0 ? (
                synonymGroups.map((group, index) => {
                  const pref =
                    optimistic[group.id] ?? groupPrefs[group.id] ?? false;
                  const isLast = index === synonymGroups.length - 1;
                  return (
                    <SynonymGroupItem
                      key={group.id}
                      synonymGroup={group}
                      isToggled={pref}
                      onToggle={() => toggleGroup(group)}
                      disabled={!!updating[group.id] || prefLoading}
                      ref={isLast ? lastItemRef : undefined}
                    />
                  );
                })
              ) : (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  {isSearching
                    ? `Ingen resultater for "${searchInput.trim()}"`
                    : "Ingen synonymer"}
                </div>
              )}
              {isLoadingMore && (
                <div className="py-2 text-center text-sm text-muted-foreground">
                  <Spinner className="size-4" /> Indlæser flere...
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SynonymButton;
